import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Copy, History, Paperclip, SendHorizonal, SquarePen } from "lucide-react";
import { Input, Spin } from "antd";
import { isMobile } from "react-device-detect";

import ChatMarkdown from "components/chat-markdown/chat-markdown";
import MarkdownWithCursor from "components/chat-window/markdown-with-cursor";
import MediaPlayer from "components/media-player/media-player";
import AiChatFeedback from "components/ai-chat-feedback/ai-chat-feedback";
import { FileAttachmentInput } from "components/file-attachment-input/file-attachment-input";
import { FileAttachmentThumbnail } from "components/file-attachment/file-attachment-thumbnail";
import { OLLIE_HEADLINES, StreamingThoughts } from "components/streaming-thoughts/streaming-thoughts";
import OllieChatHistory from "components/ollie-chat-history/ollie-chat-history";

import { useGetInputSuggestionsQuery } from "graphql/queries/get-input-suggestions.generated";

import { AiChatMessage, useAiChatConversation } from "hooks/useAiChatConversation";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useOllieScreenDescription } from "state/reactive-vars/ollie-screen-description-var";

import { AiChatType, InputSuggestionType, MediaType } from "constants/field-values";

import { stripReasoning } from "utils/chat-answer";

// A small PNG rather than ollie-owl.svg: that file wraps a very large bitmap, which the browser
// redraws blurry while the launcher is scaled or rotated.
export const OLLIE_AVATAR = `${process.env.PUBLIC_URL}/assets/images/icons/ollie-question.png`;

// Answers arrive as one long block of markdown, so the view scrolls to the bottom automatically.
// If the user scrolls up more than this many pixels from the bottom, auto-scroll stops.
const STICK_TO_BOTTOM_THRESHOLD_PX = 120;

// How long the copy button shows a tick before going back to its icon.
const COPIED_FEEDBACK_MS = 1600;

const formatTime = (createdAt?: string | null): string => {
	if (!createdAt) return "";

	const date = new Date(createdAt);

	return Number.isNaN(date.getTime()) ? "" : date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

// The fields the attachment thumbnails need to render a file, whatever shape the message used.
interface ChatAttachment {
	id?: string;
	fileName: string;
	mimeType: string;
	fileType: string;
	publicUrl?: string;
	fileKey?: string;
	gsUri?: string;
}

/*
Returns the files attached to a question. They come from two different places: the attachments the
server returns once the message is stored, and the ones staged in the browser before it is saved.
Both describe the same files with different types, so they are converted into a single shape.
*/
const readAttachments = (message: AiChatMessage): ChatAttachment[] => {
	const stored = (message.multimodal_attachments || []).filter(Boolean) as NonNullable<
		NonNullable<AiChatMessage["multimodal_attachments"]>[number]
	>[];

	const source: ChatAttachment[] = stored.length
		? stored.map(attachment => ({
				id: attachment.id,
				fileName: attachment.fileName,
				mimeType: attachment.mimeType,
				fileType: attachment.fileType,
				publicUrl: attachment.publicUrl ?? undefined,
				fileKey: attachment.fileKey ?? undefined,
				gsUri: attachment.gsUri ?? undefined
			}))
		: message.attachments || [];

	return source;
};

// A document, photo, video or guide Doctrine read to write an answer.
type ChatSource = AiChatMessage["aiChatSources"][number];

/*
What a source is called under the answer: the record it belongs to, when it has one, and the file
it was read from.
*/
const readSourceName = (source: ChatSource): string =>
	[source.property?.name || source.product?.name, source.attachment_name].filter(Boolean).join(" · ");

// Maps a MIME type to the viewer used to open that file.
const getMediaType = (mimeType: string): MediaType =>
	mimeType.startsWith("image/")
		? MediaType.Image
		: mimeType.startsWith("video/")
			? MediaType.Video
			: mimeType.startsWith("audio/")
				? MediaType.Audio
				: MediaType.Document;

interface OllieChatProps {
	// Passed by a host that can be closed, which is the floating panel. The full page version does not
	// pass it and the close button is not rendered.
	onClose?: () => void;
}

/*
Ollie, the assistant used on the inspection and plan review screens. It is a separate component and
not another mode of ChatWindow: ChatWindow is built for the customer dashboard and for the admin
Intelligence screen, while this chat is a 400px column rendered on top of another page.

It reuses the logic but not the markup. `useAiChatConversation` handles the thread, the word stream
and the attachments, and the components used below (markdown renderer, thought headlines, attachment
thumbnails, feedback control) are the same ones the other chats use.
*/
const OllieChat: React.FC<OllieChatProps> = ({ onClose }) => {
	const { loggedUserValue } = useLoggedUser();
	// Describes the screen behind the panel, so a question like "why did this fail?" has a subject.
	const { ollieScreenDescriptionValue, ollieScreenRecordsValue } = useOllieScreenDescription();
	const {
		chatId,
		messages,
		isLoadingMessages,
		loadError,
		draft,
		setDraft,
		send,
		stop,
		startNewThread,
		openThread,
		isAiResponding,
		isLongRunning,
		streamId,
		streamingText,
		streamingError,
		thoughts,
		isThinking,
		uploadingFiles,
		attachFiles,
		removeFile,
		isUploading,
		hasFiles,
		historyOpen,
		setHistoryOpen
	} = useAiChatConversation({ chatType: AiChatType.OLLIE });

	// Reasoning the model wrote into the answer does not count as the answer having started: the
	// thought headline stays up until real words arrive.
	const visibleStreamingText = useMemo(() => stripReasoning(streamingText), [streamingText]);

	const { data: suggestionsData } = useGetInputSuggestionsQuery({
		variables: { type: InputSuggestionType.OLLIE_QUERY },
		fetchPolicy: "cache-first"
	});

	const [viewerSource, setViewerSource] = useState<{ type: MediaType | null; src: string }>({ type: null, src: "" });
	// The answer whose copy button is showing a tick, and the one whose feedback form is open.
	const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
	const [feedbackMessageId, setFeedbackMessageId] = useState<string | null>(null);

	const messagesRef = useRef<HTMLDivElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const inputRef = useRef<any>(null);

	// False once the user scrolls up to read something, so new words no longer scroll the view down.
	const isPinnedToBottomRef = useRef(true);

	const handleOnScroll = useCallback(() => {
		const container = messagesRef.current;

		if (!container) return;

		isPinnedToBottomRef.current =
			container.scrollHeight - container.scrollTop - container.clientHeight <= STICK_TO_BOTTOM_THRESHOLD_PX;
	}, []);

	useEffect(() => {
		const container = messagesRef.current;

		if (!container || historyOpen || !isPinnedToBottomRef.current) return;

		container.scrollTop = container.scrollHeight;
	}, [messages, streamingText, thoughts, historyOpen]);

	useEffect(() => {
		if (!copiedMessageId) return;

		const reset = window.setTimeout(() => setCopiedMessageId(null), COPIED_FEEDBACK_MS);

		return () => window.clearTimeout(reset);
	}, [copiedMessageId]);

	// Copies the answer as it was written, which is the markdown behind what the reader sees.
	const handleOnCopyAnswer = useCallback(async (messageId: string, answer: string) => {
		try {
			await navigator.clipboard.writeText(answer);
			setCopiedMessageId(messageId);
		} catch {
			// The browser refuses the clipboard on an insecure origin. There is nothing to show for it.
		}
	}, []);

	// Opens the file a source points at. A guide is a page of its own, so it opens in a new tab.
	const handleOnOpenSource = useCallback((source: ChatSource) => {
		const { site_link, document_link, video_link, photo_link } = source;

		if (site_link) {
			window.open(site_link, "_blank", "noopener,noreferrer");
			return;
		}

		if (document_link) {
			setViewerSource({ type: MediaType.Document, src: document_link });
			return;
		}

		if (video_link) {
			setViewerSource({ type: MediaType.Video, src: video_link });
			return;
		}

		if (photo_link) setViewerSource({ type: MediaType.Image, src: photo_link });
	}, []);

	/*
	Sends the question and scrolls back to the bottom, whatever the user was reading at that moment.
	The question carries every property the user has access to: this chat has no property or device
	picker, because it is used to ask about rules and about any project that comes up. The list is
	not what the chat is scoped to — a new chat reads it to find the jurisdictions whose codes it
	may quote, and everything else about a property it queries from the database.
	*/
	const handleOnSend = useCallback(
		(text?: string) => {
			isPinnedToBottomRef.current = true;
			send(text, {
				variables: {
					selected_properties_ids: (loggedUserValue.user_properties || []).map(
						userProperty => userProperty.property_id
					),
					// Read when the message is sent, not when the panel is mounted: the user can open the
					// panel on the dashboard, navigate to a report and ask about it in the same conversation.
					screen_description: ollieScreenDescriptionValue || undefined,
					// Only a new chat reads these: they decide whose rules and codes it can draw on.
					screen_property_id: ollieScreenRecordsValue.propertyId || undefined,
					screen_jurisdiction_id: ollieScreenRecordsValue.jurisdictionId || undefined
				}
			});
		},
		[loggedUserValue.user_properties, ollieScreenDescriptionValue, ollieScreenRecordsValue, send]
	);

	useEffect(() => {
		if (!isMobile) inputRef.current?.focus();
	}, []);

	const handleOnKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
			// Enter sends the message; Shift+Enter adds a new line.
			if (event.key !== "Enter" || event.shiftKey) return;

			event.preventDefault();

			if (isUploading || isAiResponding) return;

			// Closes the on-screen keyboard, which would otherwise cover the answer being written.
			if (isMobile) event.currentTarget.blur();

			handleOnSend();
		},
		[handleOnSend, isAiResponding, isUploading]
	);

	const handleOnSelectFiles = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			await attachFiles(event.target.files);
			// Reset the input value so selecting the same file again still fires onChange.
			event.target.value = "";
		},
		[attachFiles]
	);

	const conversation = useMemo(() => {
		if (loadError) {
			return (
				<div className="oc-notice">
					<span className="oc-notice-text">{loadError}</span>
					<button type="button" className="oc-notice-action" onClick={startNewThread}>
						Start a new chat
					</button>
				</div>
			);
		}

		if (isLoadingMessages && !messages.length) {
			return (
				<div className="oc-loading">
					<Spin />
				</div>
			);
		}

		if (!messages.length) {
			const { input_suggestions = [] } = suggestionsData || {};

			return (
				<>
					<div className="oc-message oc-message-assistant">
						<div className="oc-bubble oc-bubble-assistant">
							{`Hi ${loggedUserValue.first_name || "there"}! How can I help you today?`}
						</div>
						<div className="oc-meta" />
					</div>
					{input_suggestions.length ? (
						<div className="oc-suggestions">
							{input_suggestions.map(suggestion => (
								<button
									type="button"
									key={`oc-suggestion-${suggestion.id}`}
									className="oc-suggestion"
									onClick={() => handleOnSend(suggestion.text)}
								>
									{suggestion.text}
								</button>
							))}
						</div>
					) : null}
				</>
			);
		}

		return messages.map((message: AiChatMessage) => {
			const { id, isSystem, message: text, createdAt, isPlaceholder, citations, aiChatSources } = message;

			if (!isSystem) {
				const attachments = readAttachments(message);

				const mediaSiblings = attachments
					.filter(attachment => !!attachment.publicUrl)
					.map(attachment => ({ src: attachment.publicUrl as string, mediaType: getMediaType(attachment.mimeType) }));

				return (
					<div key={`oc-message-${id}`} className="oc-message oc-message-user">
						{attachments.length ? (
							<div className="oc-attachments">
								{attachments.map((attachment, index) => (
									<FileAttachmentThumbnail
										key={`oc-attachment-${id}-${index}`}
										attachment={attachment}
										size="medium"
										showFileName={false}
										siblings={mediaSiblings.length > 1 ? mediaSiblings : undefined}
										siblingIndex={mediaSiblings.findIndex(sibling => sibling.src === attachment.publicUrl)}
									/>
								))}
							</div>
						) : null}
						<div className="oc-bubble oc-bubble-user">{text.trimStart()}</div>
						<div className="oc-meta">
							<span className="oc-timestamp">{formatTime(createdAt)}</span>
						</div>
					</div>
				);
			}

			const answer = stripReasoning(text);
			const sources = (aiChatSources || []).filter(source => !!source.attachment_name);
			const isFeedbackOpen = feedbackMessageId === id;

			return (
				<div
					key={`oc-message-${id}`}
					className={`oc-message oc-message-assistant${isFeedbackOpen ? " oc-message-feedback-open" : ""}`}
				>
					<div className="oc-bubble oc-bubble-assistant">
						{/* The reasoning is only shown while it is being written, and only as its headline. */}
						{isPlaceholder && (thoughts || isThinking) && (
							<StreamingThoughts
								thoughts={thoughts}
								hasSystemMessage={visibleStreamingText.length > 0}
								isFirstAnswer={messages.filter(entry => entry.isSystem).length <= 1}
								placeholderHeadlines={OLLIE_HEADLINES}
							/>
						)}

						{isPlaceholder ? (
							<>
								{isLongRunning && streamId && (
									<div className="oc-slow">
										<span>This is taking a while…</span>
										<button type="button" className="oc-slow-stop" onClick={stop}>
											Stop
										</button>
									</div>
								)}
								{streamingError ? (
									<div className="oc-error">{streamingError}</div>
								) : visibleStreamingText ? (
									<div className="chat-markdown">
										<MarkdownWithCursor text={visibleStreamingText} />
									</div>
								) : null}
							</>
						) : (
							<>
								<ChatMarkdown
									content={answer}
									citations={citations}
									onImageClick={src => setViewerSource({ type: MediaType.Image, src })}
								/>
								{sources.length ? (
									<div className="oc-sources">
										<span className="oc-sources-title">Sources</span>
										{sources.map(source => (
											<button
												type="button"
												key={`oc-source-${id}-${source.id}`}
												className="oc-source"
												onClick={() => handleOnOpenSource(source)}
											>
												{readSourceName(source)}
											</button>
										))}
									</div>
								) : null}
							</>
						)}
					</div>
					<div className="oc-meta">
						<span className="oc-timestamp">{formatTime(createdAt)}</span>
						{/* Copying and rating an answer are offered on hover, in the row the timestamp sits in,
						so the conversation does not move when the pointer crosses a bubble. */}
						{!isPlaceholder ? (
							<div className="oc-meta-actions">
								<button
									type="button"
									className="oc-meta-action"
									onClick={() => handleOnCopyAnswer(id, answer)}
									title="Copy"
									aria-label="Copy this answer"
								>
									{copiedMessageId === id ? <Check size={16} /> : <Copy size={16} />}
								</button>
								{chatId ? (
									<AiChatFeedback
										key={`oc-feedback-${id}`}
										doctrineChatId={chatId}
										doctrineChatMessageId={id}
										onFeedbackChange={rating => setFeedbackMessageId(rating === null ? null : id)}
									/>
								) : null}
							</div>
						) : null}
					</div>
				</div>
			);
		});
	}, [
		chatId,
		copiedMessageId,
		feedbackMessageId,
		handleOnCopyAnswer,
		handleOnOpenSource,
		handleOnSend,
		isLoadingMessages,
		isLongRunning,
		isThinking,
		loadError,
		loggedUserValue.first_name,
		messages,
		startNewThread,
		stop,
		streamId,
		streamingError,
		suggestionsData,
		thoughts,
		visibleStreamingText
	]);

	const isSendDisabled = isUploading || isAiResponding || !draft.trim();

	return (
		<div className="oc-chat">
			<div className="oc-header">
				<div className="oc-identity">
					<img src={OLLIE_AVATAR} alt="" className="oc-identity-avatar" />
					<span className="oc-identity-name">Ollie</span>
				</div>
				<div className="oc-actions">
					<button
						type="button"
						className="oc-action"
						onClick={startNewThread}
						title="New chat"
						aria-label="New chat"
					>
						<SquarePen size={18} />
					</button>
					<button
						type="button"
						className={`oc-action${historyOpen ? " oc-action-active" : ""}`}
						onClick={() => setHistoryOpen(!historyOpen)}
						title="Chat history"
						aria-label="Chat history"
						aria-pressed={historyOpen}
					>
						<History size={18} />
					</button>
					{onClose ? (
						<button type="button" className="oc-action" onClick={onClose} title="Close" aria-label="Close">
							<ChevronDown size={18} />
						</button>
					) : null}
				</div>
			</div>

			<div className="oc-body" ref={messagesRef} onScroll={handleOnScroll}>
				{historyOpen ? <OllieChatHistory onOpenThread={openThread} /> : conversation}
			</div>

			<div className="oc-composer">
				<input
					ref={fileInputRef}
					type="file"
					multiple
					accept="*/*"
					style={{ display: "none" }}
					onChange={handleOnSelectFiles}
				/>

				{uploadingFiles.length ? (
					<div className="oc-composer-attachments">
						<FileAttachmentInput uploadingFiles={uploadingFiles} onRemoveFile={removeFile} />
					</div>
				) : null}

				<Input.TextArea
					className="oc-composer-input"
					placeholder={messages.length ? "Ask a follow-up…" : "Ask anything…"}
					value={draft}
					autoComplete="off"
					autoSize={{ minRows: 1, maxRows: 6 }}
					onChange={event => setDraft(event.target.value)}
					onKeyDown={handleOnKeyDown}
					// @ts-ignore antd's TextArea ref is valid here
					ref={inputRef}
				/>

				<div className="oc-composer-actions">
					<button
						type="button"
						className={`oc-composer-attach${hasFiles ? " oc-composer-attach-active" : ""}`}
						onClick={() => fileInputRef.current?.click()}
						title="Attach a file"
						aria-label="Attach a file"
					>
						<Paperclip size={14} />
					</button>
					<button
						type="button"
						className={`oc-composer-send${!isSendDisabled ? " oc-composer-send-active" : ""}`}
						onClick={() => handleOnSend()}
						disabled={isSendDisabled}
						title="Send"
						aria-label="Send"
					>
						<SendHorizonal size={24} />
					</button>
				</div>
			</div>

			<MediaPlayer
				open={!!viewerSource.src && viewerSource.type !== null}
				src={viewerSource.src}
				mediaType={viewerSource.type}
				onCancel={() => setViewerSource({ type: null, src: "" })}
			/>
		</div>
	);
};

export default OllieChat;
